import React from "react";
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { InvoiceProduct } from "../../../../../models/Invoice/PurchaseOrderInvoice";
import { PurchaseRequestSkuVariantRequestBody } from "../../../../../models/PurchaseRequest/PurchaseRequestProductRequestBody";

interface InvoiceItemsDetailsTableProps {
  products: InvoiceProduct[];
  skuVariants?: PurchaseRequestSkuVariantRequestBody[];
}

type ProductGroup = {
  productId: number;
  productName: string;
  hasSkus: boolean;
  items: InvoiceProduct[];
};

const formatInr = (value: number): string =>
  `₹${value.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const parseQty = (item: InvoiceProduct): number => Number(item.quantity) || 0;

const parseAmount = (item: InvoiceProduct): number =>
  parseFloat(String(item.total_amount)) || 0;

const parseRate = (item: InvoiceProduct): number => {
  const qty = parseQty(item);
  if (qty <= 0) return 0;
  return parseAmount(item) / qty;
};

const itemHasSku = (item: InvoiceProduct): boolean => {
  const variantId = Number(item.variant_id);
  const hasVariant = Number.isFinite(variantId) && variantId > 0;
  const skuCode = String(item.sku_code || "").trim();
  return hasVariant || (!!skuCode && skuCode !== "null");
};

const getSkuTitle = (
  item: InvoiceProduct,
  skuVariants?: PurchaseRequestSkuVariantRequestBody[],
): string => {
  const match = (skuVariants || []).find(
    (variant) =>
      (item.variant_id != null &&
        Number(variant.variant_id) === Number(item.variant_id)) ||
      (!!item.sku_code && variant.sku === item.sku_code),
  );
  return (
    match?.title?.trim() ||
    item.variant_title?.trim() ||
    item.sku_code ||
    (item.variant_id != null ? `Variant #${item.variant_id}` : "SKU")
  );
};

const groupItemsByProduct = (items: InvoiceProduct[]): ProductGroup[] => {
  const grouped: ProductGroup[] = [];
  const indexByProduct = new Map<number, number>();

  items.forEach((item) => {
    const productId = Number(item.product_id);
    const existingIndex = indexByProduct.get(productId);
    if (existingIndex === undefined) {
      indexByProduct.set(productId, grouped.length);
      grouped.push({
        productId,
        productName: item.product_name || `Product #${productId}`,
        hasSkus: itemHasSku(item),
        items: [item],
      });
      return;
    }
    grouped[existingIndex].items.push(item);
    if (itemHasSku(item)) {
      grouped[existingIndex].hasSkus = true;
    }
  });

  return grouped;
};

export const InvoiceItemsDetailsTable = ({
  products,
  skuVariants,
}: InvoiceItemsDetailsTableProps) => {
  const theme = useTheme();
  const invoiceItems = Array.isArray(products) ? products : [];
  const hasSkuItems = invoiceItems.some(itemHasSku);

  if (invoiceItems.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 4,
        }}
      >
        <CustomTypography variant="body1" color="text.secondary">
          No product details found for this invoice
        </CustomTypography>
      </Box>
    );
  }

  if (!hasSkuItems) {
    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                Product Name
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 600, fontSize: "0.875rem" }}
              >
                Quantity
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: 600, fontSize: "0.875rem" }}
              >
                Amount
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoiceItems.map((product, index) => (
              <TableRow key={product.id || index} hover>
                <TableCell>
                  <CustomTypography variant="body2" fontWeight={500}>
                    {product.product_name || "-"}
                  </CustomTypography>
                </TableCell>
                <TableCell align="center">
                  <CustomTypography variant="body2">
                    {product.quantity || "-"}
                  </CustomTypography>
                </TableCell>
                <TableCell align="center">
                  <CustomTypography variant="body2">
                    {product.total_amount
                      ? formatInr(Number(product.total_amount))
                      : "-"}
                  </CustomTypography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  const groups = groupItemsByProduct(invoiceItems);
  const headerCellSx = { fontWeight: 600, fontSize: "0.875rem" };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>Product / Variant</TableCell>
            <TableCell sx={headerCellSx}>SKU</TableCell>
            <TableCell align="center" sx={headerCellSx}>
              Quantity
            </TableCell>
            <TableCell align="center" sx={headerCellSx}>
              Rate
            </TableCell>
            <TableCell align="center" sx={headerCellSx}>
              Amount
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => {
            if (!group.hasSkus) {
              return group.items.map((item, index) => (
                <TableRow key={item.id || `${group.productId}-${index}`} hover>
                  <TableCell>
                    <CustomTypography variant="body2" fontWeight={500}>
                      {item.product_name || "-"}
                    </CustomTypography>
                  </TableCell>
                  <TableCell>
                    <CustomTypography variant="body2">-</CustomTypography>
                  </TableCell>
                  <TableCell align="center">
                    <CustomTypography variant="body2">
                      {item.quantity || "-"}
                    </CustomTypography>
                  </TableCell>
                  <TableCell align="center">
                    <CustomTypography variant="body2">
                      {formatInr(parseRate(item))}
                    </CustomTypography>
                  </TableCell>
                  <TableCell align="center">
                    <CustomTypography variant="body2">
                      {item.total_amount
                        ? formatInr(parseAmount(item))
                        : "-"}
                    </CustomTypography>
                  </TableCell>
                </TableRow>
              ));
            }

            return (
              <React.Fragment key={group.productId}>
                <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                  <TableCell colSpan={2}>
                    <CustomTypography variant="body2" fontWeight={600}>
                      {group.productName}
                    </CustomTypography>
                  </TableCell>
                  <TableCell align="center">
                    <CustomTypography variant="body2" fontWeight={600}>
                      {group.items.reduce((sum, item) => sum + parseQty(item), 0)}
                    </CustomTypography>
                  </TableCell>
                  <TableCell />
                  <TableCell align="center">
                    <CustomTypography variant="body2" fontWeight={600}>
                      {formatInr(
                        group.items.reduce(
                          (sum, item) => sum + parseAmount(item),
                          0,
                        ),
                      )}
                    </CustomTypography>
                  </TableCell>
                </TableRow>
                {group.items.map((item, index) => (
                  <TableRow
                    key={item.id || `${group.productId}-sku-${index}`}
                    hover
                  >
                    <TableCell sx={{ pl: 4 }}>
                      <CustomTypography variant="body2" fontWeight={500}>
                        {getSkuTitle(item, skuVariants)}
                      </CustomTypography>
                    </TableCell>
                    <TableCell>
                      {item.sku_code ? (
                        <Chip
                          label={item.sku_code}
                          size="small"
                          sx={{ height: 22, fontSize: "0.75rem" }}
                        />
                      ) : (
                        <CustomTypography variant="body2">-</CustomTypography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <CustomTypography variant="body2">
                        {item.quantity || "-"}
                      </CustomTypography>
                    </TableCell>
                    <TableCell align="center">
                      <CustomTypography variant="body2">
                        {formatInr(parseRate(item))}
                      </CustomTypography>
                    </TableCell>
                    <TableCell align="center">
                      <CustomTypography variant="body2">
                        {item.total_amount
                          ? formatInr(parseAmount(item))
                          : "-"}
                      </CustomTypography>
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
